import { useEffect } from 'react';
import validators from '@sendible/common/utils/validators';
import { useToastNotificationContext } from '@sendible/design-system/src';
import { NotificationTypeEnum } from '@sendible/design-system/src/components/molecules/Toast';
import { EVENTS, publish, subscribe } from '@sendible/event-manager';
import { useBridgeContext } from '@sendible/shared-state-bridge';
import { composeMediaInteractions } from '../MediaSystem/composeMediaInteractions';
import { useComposeBoxContext } from '../MediaSystem/composeBoxContext';
import { EDITOR_CLOSE_VIDEO, EDITOR_CLOSE_WITH_ERROR, EDITOR_CLOSE, EDITOR_EXPORT, EDITOR_OPEN, EDITOR_VIDEO_EXPORT, EDITOR_VIDEO_OPEN } from '.';

export const useEditorInteractions = () => {
  const { showToastNotification } = useToastNotificationContext();
  const { activeComposeBoxTab } = useComposeBoxContext();
  const [{ user }] = useBridgeContext();

  const handleEditorOpen = (media: MediaType, mediaType: string, activeTab: composeTabs) => {
    const iframe: HTMLIFrameElement | null = document.getElementById('editor-iframe') as HTMLIFrameElement;

    const data = {
      domainUrl: window.location.origin,
      user,
      pendoUserData: window.pendoUserData,
      pendoAccountData: window.pendoAccountData,
      serviceSet: activeTab,
      restrictedServices: {
        pinterest: !window.js_featureTagsManager?.isFeatureTag('enablePinterest'),
      },
      originalMediaId: media.id,
      isNewMediaSystem: true,
      fileName: media.name,
      metadata: media.metadata,
    };

    if (iframe && iframe.contentWindow && mediaType === EVENTS.OPEN_VIDEO_EDITOR) {
      iframe.style.display = 'block';
      iframe.contentWindow.postMessage(
        {
          eventName: EDITOR_VIDEO_OPEN,
          data: {
            ...data,
            originalUrl: media.originalUrl,
            originalMediaId: media.id,
          },
        },
        '*'
      );
    }

    if (iframe && iframe.contentWindow && mediaType === EVENTS.OPEN_PHOTO_EDITOR) {
      iframe.style.display = 'block';
      iframe.contentWindow.postMessage({ eventName: 'preload-editor' }, '*');
      iframe.contentWindow.postMessage(
        {
          eventName: EDITOR_OPEN,
          data: {
            ...data,
            media,
          },
        },
        '*'
      );
    }
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const iframe: HTMLIFrameElement | null = document.getElementById('editor-iframe') as HTMLIFrameElement;

      if (validators.validatePostMessageSource(event) && iframe) {
        if (event.data.event === EDITOR_CLOSE_VIDEO || event.data.event === EDITOR_CLOSE) {
          iframe.style.display = 'none';
        }

        if (event.data.event === EDITOR_VIDEO_EXPORT || event.data.event === EDITOR_EXPORT) {
          iframe.style.display = 'none';
          composeMediaInteractions.replaceMedia(event.data.originalMediaId, event.data.media.id, activeComposeBoxTab);
          publish(EVENTS.UPLOAD_READY, event.data.media);
        }

        if (event.data.event === EDITOR_CLOSE_WITH_ERROR) {
          iframe.style.display = 'none';

          showToastNotification({
            hideTimeout: 10,
            notificationText: event.data.message,
            type: NotificationTypeEnum.Error,
            showCloseButton: true,
          });
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [activeComposeBoxTab]);

  useEffect(() => {
    const unsubscribeVideoEditor = subscribe(EVENTS.OPEN_VIDEO_EDITOR, ({ media }) => {
      handleEditorOpen(media, EVENTS.OPEN_VIDEO_EDITOR, activeComposeBoxTab);
    });

    const unsubscribePhotoEditor = subscribe(EVENTS.OPEN_PHOTO_EDITOR, ({ media }) => {
      handleEditorOpen(media, EVENTS.OPEN_PHOTO_EDITOR, activeComposeBoxTab);
    });

    return () => {
      unsubscribeVideoEditor();
      unsubscribePhotoEditor();
    };
  }, [activeComposeBoxTab]);
};
